
import React, { useMemo, useState, useEffect, useCallback } from 'react'
import {
  X,
  Search,
  Brain,
  Wrench,
  Heart,
  Loader2,
  List,
  LayoutGrid,
  Zap,
  Plus,
  Box,
  HardDrive,
  Layers,
  Sun,
  Shapes,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDrag } from 'react-dnd'
import { LIBRARY_SHADER_DRAG_TYPE } from './library-drag'
import { libraryAPI } from '@/lib/api/library'
import type { LibraryBulkFilter, LibraryEntryWithModel } from '@genieartinc/schemas'
import { AI_MODEL_CATEGORY_LABELS } from '@genieartinc/schemas'
import { useLibrarySearch } from './useLibrarySearch'
import { hasHiddenNodeTypes, isHiddenNodeType } from './hidden-node-types'
import { CommunityCard, LibraryCard, ListRow } from './library-cards'
import { ModelDetailView } from './ModelDetailView'
import { EMPTY_SELECTION, LibraryBulkBar, type LibrarySelection } from './LibraryBulkBar'
import { SketchThumbnail } from './SketchThumbnail'
import type { CatalogModel, NodeTypeFilter } from './library-types'
import {
  AVAILABLE_SKETCHES,
  AVAILABLE_SKETCH_CATEGORIES,
  SKETCH_CATEGORIES,
  type LibrarySketch,
  type SketchCategoryId,
} from '@/lib/canvas/shader/library'

// ─── Types ────────────────────────────────────────────────────────────────────

type AssetTab = 'local-models' | '3d-models' | 'materials' | 'hdris' | 'shapes'
type CommunityTab = 'model-catalog' | 'tools' | 'shaders' | 'civitai' | AssetTab
const ASSET_TABS = [
  { id: 'local-models', label: 'Local Models', Icon: HardDrive },
  { id: '3d-models', label: '3D Models', Icon: Box },
  { id: 'materials', label: 'Materials', Icon: Layers },
  { id: 'hdris', label: 'HDRIs', Icon: Sun },
  { id: 'shapes', label: 'Shapes', Icon: Shapes },
] as const

const TABS: { id: CommunityTab; label: string; Icon: React.FC<{ className?: string }> }[] = [
  { id: 'model-catalog', label: 'AI Models', Icon: Brain },
  { id: 'tools', label: 'Tools', Icon: Wrench },
  { id: 'shaders', label: 'Shaders', Icon: Zap },
]

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// ─── Native select filter ────────────────────────────────────────────────────

interface FilterSelectProps {
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
  placeholder: string
}

function FilterSelect({ value, onChange, options, placeholder }: FilterSelectProps) {
  return (
    <select
      aria-label={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(
        'h-9 rounded-lg border border-border bg-background max-w-full px-2 pr-6 text-xs appearance-none cursor-pointer flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
        'bg-[url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23888%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m7%2015%205%205%205-5%22%2F%3E%3Cpath%20d%3D%22m7%209%205-5%205%205%22%2F%3E%3C%2Fsvg%3E")] bg-[length:12px] bg-[right_8px_center] bg-no-repeat',
        value ? 'text-foreground' : 'text-foreground',
      )}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  )
}

// ─── Library Content ─────────────────────────────────────────────────────────

interface LibraryContentProps {
  communityModels?: React.ReactNode
  libraryAssets?: Partial<Record<AssetTab, React.ReactNode>>
  /** Called as a drag begins: 'shader' for a shader card, nothing for a model card. */
  onDragStart?: (kind?: 'shader') => void
  onAddToCanvas?: (model: CatalogModel) => void
  onAddShaderToCanvas?: (sketch: LibrarySketch) => void
  initialTab?: string
}

export function LibraryContent({ communityModels, libraryAssets, onDragStart, onAddToCanvas, onAddShaderToCanvas, initialTab }: LibraryContentProps) {
  const [tab, setTab] = useState<CommunityTab>((initialTab as CommunityTab) || 'model-catalog')
  useEffect(() => { if (initialTab) setTab(initialTab as CommunityTab) }, [initialTab])
  const [showMyLibrary, setShowMyLibrary] = useState(false)
  const [shaderCategory, setShaderCategory] = useState<SketchCategoryId | ''>('')
  const [shaderRuntime, setShaderRuntime] = useState<'' | 'glsl' | 'p5' | 'threejs' | 'raw'>('')

  const {
    search,
    setSearch,
    debouncedSearch,
    activeNodeType,
    setActiveNodeType,
    category,
    setCategory,
    modelFamily,
    setModelFamily,
    sort,
    setSort,
    filterOptions,
    models,
    totalCount,
    isLoading,
    isLoadingMore,
    error,
    refetch,
    isInLibrary,
    toggleLibrary,
    bulkUpdate,
    sentinelRef,
  } = useLibrarySearch()

  const [libraryModels, setLibraryModels] = useState<LibraryEntryWithModel[]>([])
  const [libraryLoading, setLibraryLoading] = useState(false)
  const [selectedModel, setSelectedModel] = useState<CatalogModel | null>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')

  // Bulk selection covers the current tab + filters; any change to them clears it
  const [selection, setSelection] = useState<LibrarySelection>(EMPTY_SELECTION)
  const bulkFilter = useMemo<LibraryBulkFilter>(() => ({
    search: debouncedSearch || undefined,
    nodeType: activeNodeType || undefined,
    ...(tab === 'tools' ? { purpose: 'tools' as const } : { modelFamily: modelFamily || undefined, purposeNot: 'tools' as const }),
  }), [debouncedSearch, activeNodeType, modelFamily, tab])
  useEffect(() => { setSelection(EMPTY_SELECTION) }, [bulkFilter, showMyLibrary])
  const isSelected = (id: string) => selection.all || selection.ids.has(id)
  const toggleSelected = (id: string) => setSelection((prev) => {
    // Unchecking one model out of "all matching" keeps the loaded rest selected
    const ids = new Set(prev.all ? models.map((m) => m.id) : prev.ids)
    if (ids.has(id)) ids.delete(id)
    else ids.add(id)
    return { all: false, ids }
  })
  const selecting = selection.all || selection.ids.size > 0

  const stableRefetch = useCallback(refetch, [refetch])
  useEffect(() => {
    if (showMyLibrary) return
    if (tab === 'model-catalog') {
      stableRefetch({
        search: debouncedSearch || undefined,
        nodeType: activeNodeType || undefined,
        category: category || undefined,
        modelFamily: modelFamily || undefined,
        purposeNot: 'tools',
        sort,
      })
    } else if (tab === 'tools') {
      stableRefetch({
        search: debouncedSearch || undefined,
        nodeType: activeNodeType || undefined,
        purpose: 'tools',
        sort,
      })
    }
    // 'shaders' tab is fully local — no API refetch
  }, [debouncedSearch, activeNodeType, category, modelFamily, sort, tab, showMyLibrary, stableRefetch])

  // Local filtering for the Shaders tab (no API call)
  const filteredShaders = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase()
    return AVAILABLE_SKETCHES.filter((s) => {
      if (shaderCategory && s.category !== shaderCategory) return false
      if (shaderRuntime && s.runtimeMode !== shaderRuntime) return false
      if (!q) return true
      return (
        s.name.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q) ||
        s.tags.some((t) => t.toLowerCase().includes(q))
      )
    })
  }, [debouncedSearch, shaderCategory, shaderRuntime])

  useEffect(() => {
    if (!showMyLibrary) return
    setLibraryLoading(true)
    libraryAPI
      .getLibrary({ search: debouncedSearch || undefined })
      .then(setLibraryModels)
      .catch(() => {})
      .finally(() => setLibraryLoading(false))
  }, [showMyLibrary, debouncedSearch])

  // "My Library" comes straight from the saved-models API, so it bypasses the
  // catalogue filter — a model saved before its node was switched off would
  // otherwise still be draggable onto the canvas.
  const visibleLibraryModels = useMemo(
    () => (hasHiddenNodeTypes
      ? libraryModels.filter((entry) => !isHiddenNodeType(entry.model.nodeType))
      : libraryModels),
    [libraryModels],
  )

  const nodeTypeOptions = [
    { value: '', label: 'All Types' },
    ...filterOptions.nodeTypes.map((t) => ({ value: t, label: capitalize(t) })),
  ]

  const familyOptions = [
    { value: '', label: 'Model Family' },
    ...filterOptions.families.map((f: string) => ({ value: f, label: f })),
  ]

  const categoryOptions = [
    { value: '', label: 'Category' },
    ...filterOptions.categories.map((c: string) => ({ value: c, label: (AI_MODEL_CATEGORY_LABELS as Record<string, string>)[c] ?? capitalize(c) })),
  ]

  const assetContent = libraryAssets?.[tab as AssetTab]
  const hostedContent = tab === 'civitai' ? communityModels : assetContent

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
          {/* Tabs */}
          <div className="px-3 py-2 flex-shrink-0 border-b border-border">
            {/* One scrolling row: wrapped tabs took up to four lines in a narrow panel. */}
            <div role="group" aria-label="Library sections" className="-mx-3 flex items-center gap-1 overflow-x-auto px-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {[TABS[0], ...ASSET_TABS.filter(item => libraryAssets?.[item.id]), ...TABS.slice(1), ...(communityModels ? [{ id: 'civitai' as const, label: 'Civitai', Icon: Brain }] : [])].map(({ id, label, Icon }) => (
                <button
                  key={id}
                  aria-pressed={tab === id && !showMyLibrary}
                  onClick={() => { setTab(id); setShowMyLibrary(false); setActiveNodeType(''); setCategory(''); setModelFamily(''); setSelectedModel(null) }}
                  className={cn(
                    'flex shrink-0 items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors whitespace-nowrap',
                    tab === id && !showMyLibrary
                      ? 'bg-muted font-medium text-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                  {id === tab && !showMyLibrary && id === 'shaders' && ` (${filteredShaders.length})`}
                  {id === tab && !showMyLibrary && (id === 'model-catalog' || id === 'tools') && totalCount > 0 && ` (${totalCount})`}
                </button>
              ))}
            </div>
          </div>

          {hostedContent ? <div key={tab} className="min-h-0 flex-1 overflow-y-auto">{hostedContent}</div> : <>
          {/* Filter bar */}
          <div className="flex flex-wrap items-center gap-2 px-3 pb-3 flex-shrink-0">
            {/* Search */}
            <div className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 h-9 min-w-0 basis-full flex-1">
              <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <input
                type="text"
                placeholder={tab === 'tools' ? 'Search tools...' : tab === 'shaders' ? 'Search shaders...' : 'Search models...'}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="min-w-0 flex-1 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
              />
              {search && (
                <button onClick={() => setSearch('')} className="text-muted-foreground hover:text-foreground">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Filter selects */}
            {tab !== 'shaders' && (
              <FilterSelect
                value={activeNodeType}
                onChange={(v) => setActiveNodeType(v as NodeTypeFilter)}
                options={nodeTypeOptions}
                placeholder="All Types"
              />
            )}
            {tab === 'shaders' && (
              <>
                <FilterSelect
                  value={shaderCategory}
                  onChange={(v) => setShaderCategory(v as SketchCategoryId | '')}
                  options={[
                    { value: '', label: 'All Categories' },
                    ...AVAILABLE_SKETCH_CATEGORIES.map((c) => ({ value: c.id, label: c.name })),
                  ]}
                  placeholder="All Categories"
                />
                <FilterSelect
                  value={shaderRuntime}
                  onChange={(v) => setShaderRuntime(v as '' | 'glsl' | 'p5' | 'threejs' | 'raw')}
                  options={[
                    { value: '', label: 'All Runtimes' },
                    { value: 'glsl', label: 'GLSL' },
                    { value: 'p5', label: 'p5.js' },
                    { value: 'threejs', label: 'Three.js' },
                    { value: 'raw', label: 'Raw JS' },
                  ]}
                  placeholder="All Runtimes"
                />
              </>
            )}
            {tab === 'model-catalog' && (
              <>
                <FilterSelect
                  value={modelFamily}
                  onChange={setModelFamily}
                  options={familyOptions}
                  placeholder="All Families"
                />
                <FilterSelect
                  value={category}
                  onChange={setCategory}
                  options={categoryOptions}
                  placeholder="All Categories"
                />
              </>
            )}

            {/* View mode toggle */}
            <div className="flex h-9 rounded-lg border border-border overflow-hidden flex-shrink-0">
              <button
                onClick={() => setViewMode('grid')}
                className={cn(
                  'flex h-full w-9 items-center justify-center transition-colors',
                  viewMode === 'grid' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-accent',
                )}
                title="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <div className="w-px bg-border" />
              <button
                onClick={() => setViewMode('list')}
                className={cn(
                  'flex h-full w-9 items-center justify-center transition-colors',
                  viewMode === 'list' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-accent',
                )}
                title="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>

            {/* My Library toggle */}
            <button
              onClick={() => setShowMyLibrary((v) => !v)}
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-lg border transition-colors flex-shrink-0',
                showMyLibrary
                  ? 'border-foreground/30 bg-muted text-foreground'
                  : 'border-border hover:bg-accent text-muted-foreground',
              )}
              title="My Library"
            >
              <Heart className={cn('h-4 w-4', showMyLibrary && 'fill-current')} />
            </button>
          </div>

          {(tab === 'model-catalog' || tab === 'tools') && !showMyLibrary && !selectedModel && (
            <LibraryBulkBar
              selection={selection}
              totalCount={totalCount}
              filter={bulkFilter}
              onSelectionChange={setSelection}
              onBulkUpdate={bulkUpdate}
            />
          )}

          {/* Scrollable content */}
          <div className="h-0 flex-1 overflow-y-auto px-4 pb-6">
            {selectedModel ? (
              <ModelDetailView
                model={selectedModel}
                isInLibrary={isInLibrary(selectedModel.id)}
                onToggleLibrary={toggleLibrary}
                onBack={() => setSelectedModel(null)}
                onAddToCanvas={onAddToCanvas ? () => { onAddToCanvas(selectedModel) } : undefined}
              />
            ) : showMyLibrary ? (
              <>
                {libraryLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : visibleLibraryModels.length > 0 ? (
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-2">
                    {visibleLibraryModels.map((entry) => (
                      <LibraryCard key={entry.id} entry={entry} onToggleLibrary={toggleLibrary} onDragStart={onDragStart} />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                    <Heart className="h-10 w-10 mb-3 opacity-30" />
                    <p className="text-sm">
                      {debouncedSearch
                        ? `No saved models matching "${debouncedSearch}"`
                        : 'Your library is empty — add models from the catalog'}
                    </p>
                  </div>
                )}
              </>
            ) : tab === 'shaders' ? (
              filteredShaders.length > 0 ? (
                <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3">
                  {filteredShaders.map((sketch) => (
                    <ShaderCard
                      key={sketch.id}
                      sketch={sketch}
                      onDragStart={onDragStart}
                      onAdd={onAddShaderToCanvas ? () => { onAddShaderToCanvas(sketch) } : undefined}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <Zap className="h-10 w-10 mb-3 opacity-30" />
                  <p className="text-sm">
                    {debouncedSearch ? `No shaders matching "${debouncedSearch}"` : 'No shaders available'}
                  </p>
                </div>
              )
            ) : (tab === 'model-catalog' || tab === 'tools') ? (
              <>
                {isLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : error ? (
                  <div className="flex flex-col items-center justify-center py-16 text-destructive">
                    <p className="text-sm font-medium">Failed to load models</p>
                    <p className="text-xs text-muted-foreground mt-1 max-w-sm text-center">{error}</p>
                  </div>
                ) : models.length > 0 ? (
                  <>
                    {viewMode === 'grid' ? (
                      <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3">
                        {models.map((model) => (
                          <CommunityCard
                            key={model.id}
                            model={model}
                            onDragStart={onDragStart}
                            isInLibrary={isInLibrary(model.id)}
                            onToggleLibrary={toggleLibrary}
                            onAddToCanvas={onAddToCanvas ? () => { onAddToCanvas(model) } : undefined}
                            onModelClick={setSelectedModel}
                            selected={isSelected(model.id)}
                            selecting={selecting}
                            onSelect={() => toggleSelected(model.id)}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1">
                        {models.map((model) => (
                          <ListRow
                            key={model.id}
                            model={model}
                            onDragStart={onDragStart}
                            isInLibrary={isInLibrary(model.id)}
                            onToggleLibrary={toggleLibrary}
                            onModelClick={setSelectedModel}
                            selected={isSelected(model.id)}
                            selecting={selecting}
                            onSelect={() => toggleSelected(model.id)}
                          />
                        ))}
                      </div>
                    )}
                    <div ref={sentinelRef} className="h-4" />
                    {isLoadingMore && (
                      <div className="flex justify-center py-4">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                    <Search className="h-10 w-10 mb-3 opacity-30" />
                    <p className="text-sm">
                      {debouncedSearch ? `No results for "${debouncedSearch}"` : 'No models available'}
                    </p>
                  </div>
                )}
              </>
            ) : null}
          </div>
        </>}
    </div>
  )
}

// ─── Shader Card ─────────────────────────────────────────────────────────────

interface ShaderCardProps {
  sketch: LibrarySketch
  onAdd?: () => void
  onDragStart?: (kind?: 'shader') => void
}

const RUNTIME_LABEL: Record<LibrarySketch['runtimeMode'], string> = {
  glsl: 'GLSL',
  p5: 'p5.js',
  threejs: 'Three.js',
  raw: 'JS',
}

function ShaderCard({ sketch, onAdd, onDragStart }: ShaderCardProps) {
  const [, drag] = useDrag(() => ({ type: LIBRARY_SHADER_DRAG_TYPE, item: () => { onDragStart?.('shader'); return { librarySketch: sketch } } }), [sketch, onDragStart])
  const categoryLabel = SKETCH_CATEGORIES.find((c) => c.id === sketch.category)?.name ?? sketch.category

  return (
    <div ref={element => { drag(element) }} aria-label={`Shader ${sketch.name}`} className="group relative flex flex-col overflow-hidden rounded-xl border border-border bg-muted/30 transition-colors hover:border-foreground/20">
      {/* Preview area — rendered thumbnail over the gradient fallback, scrimmed for legible text */}
      <div className="relative aspect-[4/5] w-full overflow-hidden bg-gradient-to-br from-fuchsia-900/40 via-violet-900/30 to-sky-900/30">
        <SketchThumbnail sketchId={sketch.id} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/45 to-black/50" />
        <div className="absolute left-2 top-2 text-[10px] font-medium uppercase tracking-wide text-white/70">
          {categoryLabel}
        </div>
        <div className="absolute right-2 top-2">
          <Zap className="h-3.5 w-3.5 text-white/60" />
        </div>
        <div className="absolute inset-x-2 bottom-2 flex flex-col gap-1">
          <div className="text-[13px] font-semibold leading-tight text-white line-clamp-2">{sketch.name}</div>
          <div className="text-[10px] text-white/70 line-clamp-2">{sketch.description}</div>
        </div>
      </div>

      {/* Footer with tags + add button */}
      <div className="flex items-center justify-between gap-2 border-t border-border bg-background px-2.5 py-1.5">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
            {RUNTIME_LABEL[sketch.runtimeMode]}
          </span>
          {sketch.inputTypes.length > 0 && (
            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
              {sketch.inputTypes[0]}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onAdd}
          disabled={!onAdd}
          className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
          aria-label={`Add ${sketch.name} to canvas`}
          title="Add to canvas"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}
